import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Filter, Loader2, AlertTriangle, RefreshCw, Search, Youtube, Facebook, Instagram, Linkedin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import PostCard from './components/PostCard';
import PostComposer from './PostComposer';
import { getPosts, getIntegrations, deletePost, publishPost } from '../../api/socialMedia';
import { PLATFORMS } from '../../utils/socialMediaHelpers';

const STATUS_OPTIONS = [
    { value: null, label: 'All Status' },
    { value: 'draft', label: 'Draft' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'published', label: 'Published' },
    { value: 'failed', label: 'Failed' },
];

const PLATFORM_ICONS = {
    youtube: Youtube,
    facebook: Facebook,
    instagram: Instagram,
    linkedin: Linkedin,
};

const PostList = () => {
    // Data state
    const [posts, setPosts] = useState([]);
    const [integrations, setIntegrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);

    // Pagination
    const [cursor, setCursor] = useState(null);
    const [hasMore, setHasMore] = useState(true);

    // Filters
    const [statusFilter, setStatusFilter] = useState(null);
    const [platformFilter, setPlatformFilter] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Composer state
    const [isComposerOpen, setIsComposerOpen] = useState(false);
    const [editingPost, setEditingPost] = useState(null);

    // Delete confirmation
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Load integrations
    useEffect(() => {
        const loadIntegrations = async () => {
            try {
                const response = await getIntegrations();
                setIntegrations(response.data || []);
            } catch (err) {
                console.error('Failed to load integrations:', err);
            }
        };
        loadIntegrations();
    }, []);

    // Load posts
    const loadPosts = useCallback(async (reset = true) => {
        try {
            setError(null);
            if (reset) {
                setLoading(true);
                setCursor(null);
            } else {
                setLoadingMore(true);
            }

            const filters = {
                limit: 20,
                cursor: reset ? null : cursor,
            };

            if (statusFilter) filters.status = statusFilter;
            if (platformFilter) {
                // Find integration ID for this platform
                const integration = integrations.find(i => i.platform === platformFilter);
                if (integration) filters.integration_id = integration.id;
            }

            const response = await getPosts(filters);

            if (reset) {
                setPosts(response.data || []);
            } else {
                setPosts(prev => [...prev, ...(response.data || [])]);
            }

            setCursor(response.cursor);
            setHasMore(response.has_more ?? false);
        } catch (err) {
            console.error('Failed to load posts:', err);
            setError(err.message || 'Failed to load posts');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [statusFilter, platformFilter, cursor, integrations]);

    // Load posts when filters change
    useEffect(() => {
        loadPosts(true);
    }, [statusFilter, platformFilter]);

    // Get integration for a post
    const getIntegration = (integrationId) => {
        return integrations.find(i => i.id === integrationId);
    };

    // Filter posts by search term (client-side)
    const filteredPosts = posts.filter(post => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return post.content?.toLowerCase().includes(term);
    });

    // Handle delete
    const handleDelete = async (post) => {
        try {
            setIsDeleting(true);
            await deletePost(post.id);
            setPosts(prev => prev.filter(p => p.id !== post.id));
            setConfirmDelete(null);
        } catch (err) {
            console.error('Failed to delete post:', err);
            setError(err.message || 'Failed to delete post');
        } finally {
            setIsDeleting(false);
        }
    };

    // Handle publish
    const handlePublish = async (post) => {
        try {
            await publishPost(post.id);
            loadPosts(true);
        } catch (err) {
            console.error('Failed to publish post:', err);
            setError(err.message || 'Failed to publish post');
        }
    };

    // Get unique platforms from integrations
    const availablePlatforms = [...new Set(integrations.map(i => i.platform))];

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                            All Posts
                        </h2>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                            {filteredPosts.length} post{filteredPosts.length !== 1 ? 's' : ''}
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => loadPosts(true)}
                            disabled={loading}
                            className="p-2 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        >
                            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        </button>

                        <button
                            onClick={() => {
                                setEditingPost(null);
                                setIsComposerOpen(true);
                            }}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-600 text-white hover:bg-yellow-700 transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            Create Post
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-3">
                    {/* Search */}
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search posts..."
                            className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                        />
                    </div>

                    {/* Status filter */}
                    <select
                        value={statusFilter || ''}
                        onChange={(e) => setStatusFilter(e.target.value || null)}
                        className="px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    >
                        {STATUS_OPTIONS.map((option) => (
                            <option key={option.label} value={option.value || ''}>
                                {option.label}
                            </option>
                        ))}
                    </select>

                    {/* Platform filter */}
                    <select
                        value={platformFilter || ''}
                        onChange={(e) => setPlatformFilter(e.target.value || null)}
                        className="px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    >
                        <option value="">All Platforms</option>
                        {availablePlatforms.map((platform) => (
                            <option key={platform} value={platform}>
                                {PLATFORMS[platform]?.name || platform}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Error display */}
            <AnimatePresence>
                {error && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="px-6 py-4 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 flex items-start gap-3"
                    >
                        <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                        <p className="text-sm text-red-700 dark:text-red-400 flex-1">{error}</p>
                        <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
                            ×
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Posts list */}
            <div className="flex-1 overflow-auto p-6">
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
                    </div>
                ) : filteredPosts.length === 0 ? (
                    <div className="text-center py-20">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                            <Filter className="w-8 h-8 text-slate-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                            No posts found
                        </h3>
                        <p className="text-slate-600 dark:text-slate-400 mb-6">
                            {searchTerm || statusFilter || platformFilter
                                ? 'Try adjusting your filters'
                                : 'Create your first post to get started'}
                        </p>
                        <button
                            onClick={() => {
                                setEditingPost(null);
                                setIsComposerOpen(true);
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-600 text-white hover:bg-yellow-700 transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            Create Post
                        </button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {filteredPosts.map((post) => (
                            <PostCard
                                key={post.id}
                                post={post}
                                integration={getIntegration(post.integration_id)}
                                onClick={() => {
                                    setEditingPost(post);
                                    setIsComposerOpen(true);
                                }}
                                onEdit={(post) => {
                                    setEditingPost(post);
                                    setIsComposerOpen(true);
                                }}
                                onDelete={(post) => setConfirmDelete(post)}
                                onPublish={handlePublish}
                            />
                        ))}

                        {/* Load more */}
                        {hasMore && (
                            <div className="text-center py-4">
                                <button
                                    onClick={() => loadPosts(false)}
                                    disabled={loadingMore}
                                    className="px-6 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-2 mx-auto"
                                >
                                    {loadingMore ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Loading...
                                        </>
                                    ) : (
                                        'Load More'
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Post Composer */}
            <PostComposer
                isOpen={isComposerOpen}
                onClose={() => {
                    setIsComposerOpen(false);
                    setEditingPost(null);
                }}
                editingPost={editingPost}
                onSave={() => loadPosts(true)}
            />

            {/* Delete Confirmation */}
            <AnimatePresence>
                {confirmDelete && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
                        onClick={() => setConfirmDelete(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full max-w-md p-6 bg-white dark:bg-slate-800 rounded-2xl shadow-xl"
                        >
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                                Delete Post?
                            </h3>
                            <p className="text-slate-600 dark:text-slate-400 mb-6">
                                Are you sure you want to delete this post? This action cannot be undone.
                            </p>
                            <div className="flex items-center justify-end gap-3">
                                <button
                                    onClick={() => setConfirmDelete(null)}
                                    className="px-4 py-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleDelete(confirmDelete)}
                                    disabled={isDeleting}
                                    className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center gap-2"
                                >
                                    {isDeleting ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Deleting...
                                        </>
                                    ) : (
                                        'Delete'
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default PostList;
